import { useMutation, type UseMutationOptions } from '@tanstack/react-query';
import { z } from 'zod';

import { ENDPOINT } from '~/constants/endpoints';

import { type SuccessResponse, zodSuccessResponseSchema } from '@vestigas/data';

import { vestigasApi } from '~/services/kyClient';

import { log } from '~/utils';

import { zodAddCompanyIdentifierPayloadSchema } from './validations';

export type AddCompanyIdentifierPayload = z.infer<
  typeof zodAddCompanyIdentifierPayloadSchema
>;

/**
 * Creates a new identifier for a company if it does not already exist.
 *
 * @param payload - The payload to add the identifier.
 * @param payload.issuerCompanyId - The ID of the sender company.
 * @param payload.identifyingText - The customer number.
 * @param payload.identifiedCompanyId - The ID of the recipient company.
 * @returns A promise that resolves when the identifier is created.
 * @throws Error if the API request fails.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Company/operation/add_company_identifier_company_identifier_post
 */
export const addCompanyIdentifier = async ({
  identifiedCompanyId,
  identifyingText,
  issuerCompanyId,
}: AddCompanyIdentifierPayload) => {
  try {
    const response = await vestigasApi
      .post(ENDPOINT.COMPANY.IDENTIFIER.CREATE(), {
        json: {
          identifiedCompanyId,
          identifyingText,
          issuerCompanyId,
        },
        validate: {
          body: zodAddCompanyIdentifierPayloadSchema,
          response: zodSuccessResponseSchema,
        },
      })
      .json<SuccessResponse>();

    return response;
  } catch (error) {
    log.error({
      message: 'Error adding company identifier',
      error,
    });

    // Re-throw the error to propagate it to the calling function.
    throw error;
  }
};

/**
 * React Query mutation hook for adding an identifier to a company.
 *
 * @param options - Additional options for the useMutation hook.
 * @returns The result of the useMutation hook.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Company/operation/add_company_identifier_company_identifier_post
 */
export const useMutationCompanyAddIdentifier = (
  options?: Omit<
    UseMutationOptions<SuccessResponse, Error, AddCompanyIdentifierPayload>,
    'mutationFn'
  >,
) => {
  return useMutation<SuccessResponse, Error, AddCompanyIdentifierPayload>({
    mutationFn: addCompanyIdentifier,
    ...options,
  });
};
